import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  Client,
  IMessage,
  StompSubscription,
} from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { KeycloakService } from 'keycloak-angular';
import { TranslateService } from '@ngx-translate/core';

type NotificationSocketSource =
  | 'onboarding'
  | 'leads'
  | 'credit';

interface SocketConfiguration {
  source: NotificationSocketSource;
  socketUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationSocketService {
  private readonly clients = new Map<
    NotificationSocketSource,
    Client
  >();

  private readonly subscriptions = new Map<
    NotificationSocketSource,
    StompSubscription
  >();

  private isConnecting = false;

  notifications$ =
    new BehaviorSubject<any[]>([]);

  newNotificationCount$ =
    new BehaviorSubject<number>(0);

  toastVisible$ =
    new BehaviorSubject<boolean>(false);

  toastTitle$ =
    new BehaviorSubject<string>('');

  toastMessage$ =
    new BehaviorSubject<string>('');

  private toastTimer:
    ReturnType<typeof setTimeout> | null =
    null;

  private readonly socketConfigurations:
    SocketConfiguration[] = [
      {
        source: 'onboarding',
        socketUrl: '/api/onboarding/ws',
      },
      {
        source: 'leads',
        socketUrl: '/api/leads/ws',
      },
      {
        source: 'credit',
        socketUrl: '/api/crdt-prpsl/ws',
      },
    ];

  constructor(
    private keycloak: KeycloakService,
    private translate: TranslateService,
  ) {}

  async connect(): Promise<void> {
    if (this.isConnecting) {
      console.log(
        'WebSocket connection is already being initialized.',
      );

      return;
    }

    this.isConnecting = true;

    try {
      console.log(
        'Starting notification WebSocket connections...',
      );

      const token =
        await this.keycloak.getToken();

      if (!token) {
        console.error(
          'WebSocket connection failed: Keycloak token is missing.',
        );

        return;
      }

      const keycloakInstance =
        this.keycloak.getKeycloakInstance();

      const tokenParsed: any =
        keycloakInstance.tokenParsed;

      const username = String(
        tokenParsed?.preferred_username ||
          tokenParsed?.username ||
          tokenParsed?.sub ||
          '',
      ).trim();

      if (!username) {
        console.error(
          'WebSocket connection failed: username is missing from Keycloak token.',
        );

        return;
      }

      console.log(
        'WebSocket user resolved:',
        username,
      );

      this.socketConfigurations.forEach(
        (configuration) => {
          this.connectSocket(
            configuration,
            username,
            token,
          );
        },
      );
    } catch (error) {
      console.error(
        'Unable to initialize notification WebSockets:',
        error,
      );
    } finally {
      this.isConnecting = false;
    }
  }

  private connectSocket(
    configuration: SocketConfiguration,
    username: string,
    initialToken: string,
  ): void {
    const existingClient =
      this.clients.get(
        configuration.source,
      );

    if (
      existingClient?.active ||
      existingClient?.connected
    ) {
      console.log(
        `${configuration.source.toUpperCase()} WebSocket is already active.`,
        {
          active: existingClient.active,
          connected:
            existingClient.connected,
        },
      );

      return;
    }

    console.log(
      `Connecting ${configuration.source.toUpperCase()} WebSocket...`,
      {
        socketUrl:
          configuration.socketUrl,
        username,
      },
    );

    const client = new Client({
      webSocketFactory: () => {
        console.log(
          `Creating ${configuration.source.toUpperCase()} SockJS connection:`,
          configuration.socketUrl,
        );

        return new SockJS(
          configuration.socketUrl,
        );
      },

      connectHeaders: {
        Authorization:
          `Bearer ${initialToken}`,
      },

      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,

      debug: (message: string) => {
        console.debug(
          `[${configuration.source.toUpperCase()} STOMP]`,
          message,
        );
      },
    });

    client.beforeConnect =
      async (): Promise<void> => {
        try {
          console.log(
            `${configuration.source.toUpperCase()} WebSocket preparing connection...`,
          );

          const freshToken =
            await this.keycloak.getToken();

          if (!freshToken) {
            console.error(
              `${configuration.source.toUpperCase()} WebSocket token is missing.`,
            );

            return;
          }

          client.connectHeaders = {
            Authorization:
              `Bearer ${freshToken}`,
          };

          console.log(
            `${configuration.source.toUpperCase()} WebSocket token updated before connection.`,
          );
        } catch (error) {
          console.error(
            `${configuration.source.toUpperCase()} WebSocket token refresh failed:`,
            error,
          );
        }
      };

    client.onConnect = (frame) => {
      console.log(
        `${configuration.source.toUpperCase()} WebSocket connected successfully.`,
        {
          socketUrl:
            configuration.socketUrl,
          active: client.active,
          connected: client.connected,
          session:
            frame.headers?.['session'],
          server:
            frame.headers?.['server'],
          version:
            frame.headers?.['version'],
        },
      );

      this.subscribeToUserAlerts(
        client,
        configuration.source,
        username,
      );
    };

    client.onStompError = (frame) => {
      console.error(
        `${configuration.source.toUpperCase()} STOMP error.`,
        {
          message:
            frame.headers?.['message'],
          body: frame.body,
          headers: frame.headers,
        },
      );
    };

    client.onWebSocketError = (
      error: Event,
    ) => {
      console.error(
        `${configuration.source.toUpperCase()} WebSocket error.`,
        error,
      );
    };

    client.onWebSocketClose = (
      event: CloseEvent,
    ) => {
      console.warn(
        `${configuration.source.toUpperCase()} WebSocket connection closed.`,
        {
          code: event.code,
          reason:
            event.reason ||
            'No reason provided',
          clean: event.wasClean,
          active: client.active,
          connected: client.connected,
        },
      );
    };

    client.onDisconnect = (frame) => {
      console.log(
        `${configuration.source.toUpperCase()} WebSocket disconnected.`,
        {
          headers: frame.headers,
          body: frame.body,
        },
      );

      this.subscriptions.delete(
        configuration.source,
      );
    };

    this.clients.set(
      configuration.source,
      client,
    );

    console.log(
      `Activating ${configuration.source.toUpperCase()} WebSocket client...`,
    );

    client.activate();
  }

  private subscribeToUserAlerts(
    client: Client,
    source: NotificationSocketSource,
    username: string,
  ): void {
    const existingSubscription =
      this.subscriptions.get(source);

    if (existingSubscription) {
      console.log(
        `Removing existing ${source.toUpperCase()} notification subscription.`,
      );

      existingSubscription.unsubscribe();
    }

    const destination =
      `/queue/alerts/${username}`;

    console.log(
      `Subscribing to ${source.toUpperCase()} notification destination:`,
      destination,
    );

    const subscription =
      client.subscribe(
        destination,
        (message: IMessage) => {
          console.log(
            `${source.toUpperCase()} notification received.`,
            {
              destination,
              body: message.body,
              headers: message.headers,
            },
          );

          this.handleIncomingNotification(
            message,
            source,
          );
        },
      );

    this.subscriptions.set(
      source,
      subscription,
    );

    console.log(
      `${source.toUpperCase()} notification queue subscribed successfully.`,
      {
        destination,
        subscriptionId:
          subscription.id,
      },
    );
  }

  private handleIncomingNotification(
    message: IMessage,
    source: NotificationSocketSource,
  ): void {
    try {
      const parsedNotification =
        JSON.parse(message.body);

      const notification = {
        ...parsedNotification,
        socketSource: source,
        receivedAt:
          new Date().toISOString(),
      };

      if (
        this.isDuplicateNotification(
          notification,
        )
      ) {
        console.warn(
          `${source.toUpperCase()} duplicate notification ignored.`,
          notification,
        );

        return;
      }

      this.notifications$.next([
        notification,
        ...this.notifications$.value,
      ]);

      this.newNotificationCount$.next(
        this.newNotificationCount$
          .value + 1,
      );

      console.log(
        `${source.toUpperCase()} notification added to local state.`,
        {
          notification,
          totalNotifications:
            this.notifications$.value
              .length,
          unreadCount:
            this.newNotificationCount$
              .value,
        },
      );

      const title =
        notification.title ||
        notification.ttl ||
        notification.subject ||
        notification.ntfctnTitle ||
        this.translate.instant(
          source === 'leads'
            ? 'NOTIFICATIONS.LEAD_NOTIFICATION'
            : source === 'credit'
              ? 'NOTIFICATIONS.CREDIT_PROPOSAL_NOTIFICATION'
              : 'NOTIFICATIONS.ONBOARDING_NOTIFICATION',
        );

      const notificationMessage =
        notification.message ||
        notification.msg ||
        notification.cmmnt ||
        notification.comment ||
        notification.description ||
        notification.dscr ||
        notification.ntfctnDscr ||
        this.translate.instant('NOTIFICATIONS.NEW_NOTIFICATION_RECEIVED');

      this.showToast(
        String(title),
        String(notificationMessage),
      );
    } catch (error) {
      console.error(
        `Unable to parse ${source.toUpperCase()} notification.`,
        {
          body: message.body,
          error,
        },
      );
    }
  }

  private isDuplicateNotification(
    notification: any,
  ): boolean {
    const notificationId =
      notification?.ntfctnSeq ??
      notification?.notificationId ??
      notification?.id ??
      notification?.alertId;

    if (
      notificationId === null ||
      notificationId === undefined
    ) {
      return false;
    }

    return this.notifications$.value.some(
      (existingNotification: any) => {
        const existingId =
          existingNotification
            ?.ntfctnSeq ??
          existingNotification
            ?.notificationId ??
          existingNotification?.id ??
          existingNotification?.alertId;

        return (
          String(existingId) ===
          String(notificationId)
        );
      },
    );
  }

  logConnectionStatus(): void {
    console.log(
      'Notification WebSocket connection status:',
    );

    this.socketConfigurations.forEach(
      (configuration) => {
        const client =
          this.clients.get(
            configuration.source,
          );

        const subscription =
          this.subscriptions.get(
            configuration.source,
          );

        console.log(
          `${configuration.source.toUpperCase()} WebSocket status`,
          {
            socketUrl:
              configuration.socketUrl,
            clientCreated: !!client,
            active:
              client?.active ?? false,
            connected:
              client?.connected ?? false,
            subscribed:
              !!subscription,
            subscriptionId:
              subscription?.id ?? null,
          },
        );
      },
    );
  }

  isConnected(
    source: NotificationSocketSource,
  ): boolean {
    const client =
      this.clients.get(source);

    return Boolean(
      client?.active &&
        client?.connected,
    );
  }

  async disconnect(): Promise<void> {
    console.log(
      'Disconnecting notification WebSockets...',
    );

    this.subscriptions.forEach(
      (
        subscription,
        source,
      ) => {
        try {
          subscription.unsubscribe();

          console.log(
            `${source.toUpperCase()} notification subscription removed.`,
          );
        } catch (error) {
          console.warn(
            `${source.toUpperCase()} subscription was already closed.`,
            error,
          );
        }
      },
    );

    this.subscriptions.clear();

    const deactivateRequests =
      Array.from(
        this.clients.entries(),
      ).map(
        async ([source, client]) => {
          if (
            client.active ||
            client.connected
          ) {
            console.log(
              `Deactivating ${source.toUpperCase()} WebSocket...`,
            );

            await client.deactivate();

            console.log(
              `${source.toUpperCase()} WebSocket deactivated successfully.`,
            );
          }
        },
      );

    await Promise.all(
      deactivateRequests,
    );

    this.clients.clear();

    console.log(
      'All notification WebSockets disconnected.',
    );
  }

  showToast(
    title: string,
    message: string,
  ): void {
    console.log(
      'Showing notification toast.',
      {
        title,
        message,
      },
    );

    this.toastTitle$.next(title);
    this.toastMessage$.next(message);
    this.toastVisible$.next(true);

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(
      () => {
        this.toastVisible$.next(false);
        this.toastTimer = null;

        console.log(
          'Notification toast automatically closed.',
        );
      },
      5000,
    );
  }

  dismissToast(): void {
    this.toastVisible$.next(false);

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    console.log(
      'Notification toast dismissed.',
    );
  }

  resetNewCount(): void {
    this.newNotificationCount$.next(0);

    console.log(
      'New notification count reset.',
    );
  }

  clearNotifications(): void {
    this.notifications$.next([]);
    this.resetNewCount();

    console.log(
      'All local notifications cleared.',
    );
  }
}